import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ITemplateConditionNodeDto, ILogicTypeEnum } from '../../../../interfaces/IUtilities';
import * as Yup from 'yup';
import { useToast } from '../ToastContext';
import { buildClaimIdErrorMap, collectClaimIds, getAllClaims, mapAllClaims, stringifyCondition } from '../../../../helpers/ClaimBuilderHelper';
import TemplateClaimNodeEditor, {  TemplateClaimNodeEditorHandle } from './TemplateClaimNodeEditor';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ProfileType } from '../../../../constants/constants';

// ==================== Schema ====================
export const claimNodeSchema = (claimMap: Record<string, string>): Yup.ObjectSchema<ITemplateConditionNodeDto> =>
  Yup.object({
    id: Yup.string().required(),
    templateClaimId: Yup.string().required(),
    parentId: Yup.string().nullable(),
    logicType: Yup.number().oneOf([ILogicTypeEnum.AND, ILogicTypeEnum.OR, ILogicTypeEnum.LEAF, ILogicTypeEnum.ANDNOT, ILogicTypeEnum.ORNOT]).required(),
    claimId: Yup.string()
      .nullable()
      .test('is-valid-claim', i18next.t('FieldRequired', { field: i18next.t('Claim') }), function (value) {
        const { logicType } = this.parent;
        return logicType === ILogicTypeEnum.LEAF ? !!value?.trim() : true;
      }),
    orderIndex: Yup.number().required(),
    // customValue: Yup.string()
    //   .nullable()
    //   .test(
    //     'required-if-profile-claim',
    //     i18next.t('FieldRequired', { field: i18next.t('ProfileType') }),
    //     function (value) {
    //       const claim = this.parent as ITemplateConditionNodeDto;
    //       const claimId = String(claim.claimId);
    //       const claimType = claimMap[claimId] || '';
    //       const needsProfile = claimType.toLocaleLowerCase().includes(ProfileType);

    //       if (needsProfile) {
    //         return Boolean(value && value.trim());
    //       }
    //       return true;
    //     }
    //   ), //Set profile type Required
    children: Yup.lazy((_, { parent }) => {
      if (parent.logicType === ILogicTypeEnum.LEAF) {
        return Yup.array().length(0);
      }
      return Yup.array().of(claimNodeSchema(claimMap));
    })
}) as Yup.ObjectSchema<ITemplateConditionNodeDto>;


// ==================== TemplateClaimBuilder ====================
export interface TemplateClaimBuilderHandle {
  validate: () => Promise<boolean>;
}

interface ClaimBuilderProps {
  rootNode: ITemplateConditionNodeDto;
  setRootNode: (node: ITemplateConditionNodeDto) => void;
  isEdit: boolean
}

const TemplateClaimBuilder = forwardRef<TemplateClaimBuilderHandle, ClaimBuilderProps>(
  ({ rootNode, setRootNode, isEdit }, ref) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [mode, setMode] = useState<'single' | 'multi'>(rootNode.logicType === ILogicTypeEnum.LEAF ? 'single' : 'multi');
    const editorRefs = useRef<Record<string, TemplateClaimNodeEditorHandle | null>>({});

    const registerEditorRef = useCallback((id: string, ref: TemplateClaimNodeEditorHandle | null) => {
        if (ref) editorRefs.current[id] = ref;
        else delete editorRefs.current[id];
    }, []);
    
    const resetAllChildren = () => {
      Object.values(editorRefs.current).forEach((ref) => {
        ref?.reset?.();
      });
    };

    const [claimMap, setClaimMap] = useState({} as Record<string, string>);

    useImperativeHandle(ref, () => ({
      validate: async () => {
        Object.values(editorRefs.current).forEach((ref) => {
            ref?.forceShowError();
        });

        try {
          const schema = claimNodeSchema(claimMap);
          await schema.validate(rootNode, { abortEarly: false });

          if (mode === 'single') {
            const ids = collectClaimIds(rootNode).filter(Boolean);
            if (ids.length !== 1) {
              setValidationErrors(buildClaimIdErrorMap(rootNode, t('OneClaimRequiredInSingleClaimMode')));
              return false;
            }
          }

          setValidationErrors({});
          return true;
        } catch (err: any) {
            if (err.inner) {
              const fieldErrors: Record<string, string> = {};
              err.inner.forEach((e: any) => {
                if (e.path?.includes('claimId')) {
                  fieldErrors[e.path] = e.message;
                }
              });
              setValidationErrors(fieldErrors);
            }
            return false;
        }
      },
    }));

    const switchToGroup = () => {
      resetAllChildren();

      const leafChild: ITemplateConditionNodeDto = {
        id: uuidv4(),
        templateClaimId: rootNode.templateClaimId,
        parentId: rootNode.id,
        logicType: ILogicTypeEnum.LEAF,
        claimId: null, //claimId: rootNode.claimId || '', //if we want to keep the claimId
        orderIndex: 1,
        negated: false,
        customValue: null,
        children: []
      };

      setRootNode({
        ...rootNode,
        logicType: ILogicTypeEnum.AND,
        claimId: null,
        negated: false,
        customValue: null,
        children: [leafChild],
      });

      setMode('multi');
    };

    const switchToLeaf = () => {
      if (!rootNode.children || rootNode.children.length !== 1) {
        showToast(t('CanOnlySwitchToASingleClaim'), 'warning');
        return;
      }

      //const child = rootNode.children[0];
      setRootNode({
        ...rootNode,
        logicType: ILogicTypeEnum.LEAF,
        claimId: null, //claimId: child.claimId || '', //if we want to keep the claimId
        children: [],
      });

      setMode('single');
    };

    const resetBuilder = () => {
      resetAllChildren();

      setMode('single');
      setRootNode({
          id: uuidv4(),
          templateClaimId: rootNode.templateClaimId,
          parentId: null,
          logicType: ILogicTypeEnum.LEAF,
          claimId: null,
          orderIndex: 1,
          negated: false,
          customValue: null,
          children: []
      });
    };

    useEffect(() => {
      const fetchClaimNames = async () => {
          const data = await getAllClaims();
          setClaimMap(mapAllClaims(data));
      };

      fetchClaimNames();

      if (rootNode.logicType === ILogicTypeEnum.LEAF) {
        setMode('single');
      } else {
        setMode('multi');
      }
    }, [rootNode.logicType]);

  
    return (
      <div className="gg-container-claimBuilder">
        <h3>{t('ClaimLogicBuilder')}</h3>

        <div className="mb-3 d-flex gap-2">
          {mode === 'single' ? (
            <button type="button" className="btn gg-btn-black" onClick={switchToGroup}>
              {t('SwitchToMultiClaimMode')}
            </button>
          ) : (
            <button type="button" className="btn gg-btn-black" onClick={switchToLeaf}>
              {t('SwitchToSingleClaimMode')}
            </button>
          )}

            <button type="button" className="btn btn-danger" onClick={resetBuilder}>
              {t('ResetBuilder')}
            </button>
        </div>

        <div className="mt-3">
          <h5>{t('Preview')}</h5>
          <div className="alert alert-secondary">
            <pre className="mb-0">{stringifyCondition(rootNode, claimMap)}</pre>
          </div>
        </div>

        <TemplateClaimNodeEditor
            registerRef={registerEditorRef}
            node={rootNode}
            onChange={setRootNode}
            onRemove={() =>{
                setMode('single');
                setRootNode({
                    id: uuidv4(),
                    templateClaimId: rootNode.templateClaimId,
                    parentId: null,
                    logicType: ILogicTypeEnum.LEAF,
                    claimId: null,
                    orderIndex: 1,
                    negated: false,
                    customValue: null,
                    children: []
                })}
            }
            validationErrors={validationErrors}
            ref={(ref) => {
              if (ref) editorRefs.current[rootNode.id] = ref;
              else delete editorRefs.current[rootNode.id];
            }}
            onClaimMapUpdate={setClaimMap}
            isEdit={isEdit}
        />

        <div className="mt-4">
          <h5>{t('JSONPreview')}</h5>
          <div className="table-responsive">
            <pre className="bg-light p-3 border rounded">
              {JSON.stringify(rootNode, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }
);

export default TemplateClaimBuilder;
